// Anita Week 1 / Day 3 — Need, Want and a Plan (U.S. Grade 3 math upgrade).
// Math is Grade 3: multi-line addition with three items, two-option comparison
// with adjusted totals, simple fraction 1/3 of a budget, and a price-table
// problem. Russian stays at RU Grade 1–2. SAFETY: paper simulations only.

const day3 = {
  id: 'day-3',
  studentId: 'anita',
  weekId: 'week-1',
  dayId: 'day-3',
  status: 'active',
  title: 'Нужное, желаемое и план / Need, Want and a Plan',
  shortTitle: 'Нужное, хочу, план / Need, Want, Plan',
  theme:
    'План помогает не потратить всё сразу. Сначала нужное, потом полезное, потом «хочу». / A plan helps you not spend everything at once. Need first, then useful, then "want".',
  estimatedTimeLabel: '60–90 минут / 60–90 minutes',
  lessonTiming: { minMinutes: 60, maxMinutes: 90, breaks: 2 },
  bilingualMode: 'ru-primary-en-helper',
  courseLayers: ['reading', 'math', 'logic', 'spelling', 'creative', 'speech'],
  readingLevel: 'grade-2',
  nimnyIntro:
    'Нимни приносит блокнот для плана. Сегодня Анита строит свой первый бюджет: нужное, полезное и «хочу». Она считает остаток и думает, что важнее. / Nimny brings a planning notebook. Today Anita builds her first budget: need, useful, want. She counts the remainder and thinks about priorities.',
  goals: [
    'Понять разницу между «нужное», «полезное» и «хочу». / Understand need, useful and want.',
    'Считать стоимость плана в 3 предмета и остаток. / Add a 3-item plan and find the remainder.',
    'Сравнивать два плана и находить, какой дешевле. / Compare two plans and find which is cheaper.',
    'Работать с дробью 1/3 от бюджета. / Work with 1/3 of a budget.',
    'Объяснять, почему план умнее, чем «купить всё сразу». / Explain why a plan is smarter than buying all at once.'
  ],
  vocabulary: [
    { term: 'нужное / need', definition: 'без этого нельзя обойтись. / something you cannot do without.' },
    { term: 'полезное / useful', definition: 'помогает учиться или жить лучше. / helps you learn or live better.' },
    { term: 'хочу / want', definition: 'мне приятно, но не обязательно. / nice to have, not required.' },
    { term: 'план / plan', definition: 'список выборов и порядок шагов. / a list of choices and steps.' },
    { term: 'бюджет / budget', definition: 'сколько у меня нимов на покупки. / how many nims I have to spend.' },
    { term: 'осталось / remained', definition: 'сколько нимов остаётся после плана. / how many nims remain after the plan.' }
  ],
  onlineTasks: [
    {
      id: 'reading-words',
      type: 'text',
      title: 'Слова дня / Words of the Day',
      prompt: 'Прочитай: нужное, полезное, хочу, план, бюджет, осталось.',
      ruPrimaryText: 'Прочитай по-русски: нужное, полезное, хочу, план, бюджет, осталось.',
      enHelpText: 'Read in Russian: need, useful, want, plan, budget, remained.',
      hint: 'Сначала русское чтение. / Russian first.',
      answer: 'Попытка чтения русских слов. / Attempted Russian reading.'
    },
    {
      id: 'reading-sentences',
      type: 'text',
      title: 'Маленькое чтение / Short Reading',
      prompt:
        'Прочитай по-русски:\n1. План помогает не потратить всё сразу.\n2. Сначала нужное, потом полезное, потом «хочу».\n3. Я считаю стоимость плана и остаток.\n4. Я сравниваю два плана.',
      ruPrimaryText:
        'Прочитай 4 короткие фразы про план и бюджет.',
      enHelpText:
        '1. A plan helps you not spend everything at once.\n2. Need first, then useful, then "want".\n3. I add up the plan cost and the remainder.\n4. I compare two plans.',
      answer: 'Попытка чтения 4 русских фраз. / Attempted Russian reading.'
    },
    {
      id: 'math-1-plan-120',
      type: 'math',
      title: 'Math 1 — План на 120 нимов / Plan for 120 Nims',
      prompt:
        'У Аниты 120 нимов.\nОна выбирает:\nнужное — тетрадь за 28 нимов,\nполезное — книга за 36 нимов,\nхочу — игрушка за 45 нимов.\nЕсли она купит всё, сколько останется?',
      ruPrimaryText:
        '120 нимов. 28 + 36 + 45. Сколько останется?',
      enHelpText:
        '120 nims. Notebook 28, book 36, toy 45. If she buys all, how much remains?',
      helper: 'Сначала сумма трёх покупок, потом 120 минус сумма. / Sum first, then subtract.',
      answer: '11',
      unit: 'нимов / nims'
    },
    {
      id: 'math-2-compare-plans',
      type: 'math',
      title: 'Math 2 — Сравни два плана / Compare Two Plans',
      prompt:
        'У Аниты 100 нимов.\nВариант A: тетрадь 28 + книга 36 + наклейки 18.\nВариант B: тетрадь 28 + игрушка 45 + сок 17.\nКакой вариант дешевле и на сколько?',
      ruPrimaryText:
        '100 нимов. A: 28 + 36 + 18. B: 28 + 45 + 17. Какой дешевле и на сколько?',
      enHelpText:
        '100 nims. Option A: 28 + 36 + 18. Option B: 28 + 45 + 17. Which is cheaper and by how many nims?',
      helper: 'Посчитай сумму A, сумму B, потом сравни. / Sum each, then compare.',
      answer: 'A дешевле на 8 / A cheaper by 8',
      unit: 'нимов / nims'
    },
    {
      id: 'math-3-one-third-budget',
      type: 'math',
      title: 'Math 3 — Треть бюджета / One Third of the Budget',
      prompt:
        'У Аниты 90 нимов.\nОна хочет отложить 1/3.\nСколько она отложит?\nСколько останется для покупок?\n\nЗапиши ответ через /: отложит / останется. Например: 30 / 60.',
      ruPrimaryText:
        '1/3 от 90 = ?. 90 − 1/3 = ?. Ответ через /: 30 / 60.',
      enHelpText:
        '90 nims. She wants to save 1/3. How many will she save? How many remain for shopping? Write the answer with a slash: save / remain. Example: 30 / 60.',
      helper: '1/3 от 90 — это 90 ÷ 3. Ответ через /: отложит / останется. / 1/3 of 90 is 90 ÷ 3. Format as save / remain.',
      answer: '30 / 60',
      unit: ''
    },
    {
      id: 'math-4-price-table-budget',
      type: 'math',
      title: 'Math 4 — Таблица выбора / Choice Table',
      prompt:
        'Таблица:\nручка — 12\nтетрадь — 24\nкнига — 36\nнаклейки — 15\nсок — 9\n\nАнита выбрала тетрадь, книгу и сок.\nСколько стоит выбор?\nЕсли бюджет 80, хватит ли? Сколько останется?\n\nЗапиши ответ через /: стоимость / остаток. Например: 69 / 11.',
      ruPrimaryText:
        'Тетрадь + книга + сок. Бюджет 80. Стоимость? Остаток? Ответ через /: 69 / 11.',
      enHelpText:
        'Use the table. Notebook 24 + book 36 + juice 9. Budget 80. Is it enough? How many remain? Write the answer with a slash: cost / remain. Example: 69 / 11.',
      helper: 'Сложи 3 цены, потом 80 минус сумма. Ответ через /: стоимость / остаток. / Sum 3 prices, then subtract from 80. Format as cost / remain.',
      answer: '69 / 11',
      unit: ''
    },
    {
      id: 'logic-plan-1-vs-plan-2',
      type: 'text',
      title: 'Логика — какой план умнее? / Logic — Which Plan Is Smarter?',
      prompt:
        'План 1: купить сразу всё.\nПлан 2: купить нужное сейчас, а «хочу» позже.\nКакой план умнее и почему?\nОтветь 1–2 предложениями.',
      ruPrimaryText:
        'План 1: всё сразу. План 2: нужное сейчас, «хочу» позже. Какой умнее? Объясни.',
      enHelpText:
        'Plan 1: buy everything at once. Plan 2: buy the need now and the want later. Which plan is smarter and why? Answer in 1–2 sentences.',
      placeholder: 'План 2 умнее, потому что ...',
      answer:
        'Принимается ответ, где «План 2» (или равноценный) выбран с объяснением: «остаётся остаток на цель», «нужное важнее», «хочу можно подождать».'
    },
    {
      id: 'spelling-plan',
      type: 'text',
      title: 'Правописание / Spelling',
      prompt:
        'Напиши:\nнужное\nполезное\nплан\nбюджет\n\nВставь буквы:\nну_ное\nпо_езное\nпл_н\nб_джет',
      ruPrimaryText: 'Напиши слова и вставь пропущенные буквы.',
      enHelpText: 'Write the words and fill in the missing letters: нужное, полезное, план, бюджет.',
      answer: 'нужное, полезное, план, бюджет.'
    },
    {
      id: 'creative-my-plan',
      type: 'text',
      title: 'Мой план на 100 нимов / My 100-Nim Plan',
      prompt:
        'Нарисуй или составь свой план на 100 нимов.\nЗаполни:\nНужное: ______ — ___ нимов.\nПолезное: ______ — ___ нимов.\nХочу: ______ — ___ нимов.\nОстаток: ___ нимов.',
      ruPrimaryText: 'Составь свой план на 100 нимов: нужное, полезное, хочу и остаток.',
      enHelpText: 'Plan your 100 nims: need, useful, want and the remainder.',
      answer:
        'Open-ended. Проверить, что сумма + остаток = 100 и есть все три категории.'
    },
    {
      id: 'speech-explain-plan',
      type: 'text',
      title: 'Объясни родителю / Explain to Parent',
      prompt: 'Скажи по-русски: почему план помогает не потратить всё сразу?',
      ruPrimaryText: 'Скажи по-русски: «План помогает, потому что …».',
      enHelpText: 'Say in Russian: "A plan helps because …".',
      answer:
        'Принимается любое осмысленное продолжение фразы, где упоминаются «нужное» и «остаток» или «цель».'
    }
  ],
  notebookInstructions: [
    'Если нет принтера:',
    '1. Напиши слова дня.',
    '2. Реши Math 1–4 с шагами.',
    '3. Объясни, какой план умнее и почему.',
    '4. Напиши 4 слова на правописание.',
    '5. Составь свой план на 100 нимов: нужное, полезное, хочу и остаток.',
    '6. Скажи родителю по-русски: «План помогает, потому что …».'
  ],
  printWorksheet: {
    title: 'Нужное, желаемое и план / Need, Want and a Plan',
    subtitle: 'Неделя 1 · День 3 / Week 1 · Day 3',
    pages: [
      {
        title: 'Маршрут дня · Слова · Чтение / Route, Words and Reading',
        blocks: [
          { type: 'text', text: 'Миссия: Анита строит свой первый план на 100–120 нимов. / Mission: Anita builds her first 100–120 nim plan.' },
          { type: 'list', items: [
            'Слова дня: нужное, полезное, хочу, план, бюджет, осталось. / Words of the day.',
            'Маленькое чтение — 4 короткие фразы про план (см. экран). / Short Russian reading.',
            'English help мелким шрифтом. / English help in small print.'
          ] }
        ]
      },
      {
        title: 'Математика плана / Plan Math',
        blocks: [
          { type: 'text', text: 'Показывай шаги. Сначала сумма, потом остаток. / Show steps. Sum first, then remainder.' },
          { type: 'list', items: [
            'Math 1. 120 − (28 + 36 + 45) = ___. Шаги: 28 + 36 = ___; ___ + 45 = ___; 120 − ___ = ___. / Plan for 120 nims.',
            'Math 2. A = 28 + 36 + 18 = ___; B = 28 + 45 + 17 = ___; ___ − ___ = ___. Какой дешевле? / Compare two plans.',
            'Math 3. 1/3 от 90 = 90 ÷ 3 = ___. Останется = 90 − ___ = ___. / One third of the budget.',
            'Math 4. 24 + 36 + 9 = ___; 80 − ___ = ___. Хватит ли? / Choice table.',
            'Логика: какой план умнее — «всё сразу» или «нужное сейчас, хочу позже»? Ответ 1–2 предложения. / Logic.'
          ] }
        ]
      },
      {
        title: 'Правописание, мой план-проект и parent check / Spelling, My Plan-Project and Parent Check',
        blocks: [
          { type: 'list', items: [
            'Правописание: напиши нужное, полезное, план, бюджет. Вставь буквы: ну_ное, по_езное, пл_н, б_джет. / Spelling.',
            'Нарисуй или составь мой план-проект на 100 нимов: нужное ___ (___), полезное ___ (___), хочу ___ (___), остаток ___ . / Draw or build my 100-nim plan-project with all three categories and the remainder.',
            'Скажи родителю по-русски: «План помогает, потому что …». / Say in Russian to a parent.',
            'Parent Check: проверить сумму, остаток, дробь 1/3 и логику плана. / Parent Check.',
            'Notebook Mode fallback. / Notebook Mode fallback.'
          ] }
        ]
      }
    ]
  },
  parentGuide: {
    goal:
      'Анита строит план на бюджет, сравнивает два плана, считает 1/3 бюджета и объясняет, почему «нужное» идёт первым.',
    mathGoal:
      'U.S. Grade 3: суммы из трёх слагаемых, сравнение двух планов по сумме, дробь 1/3 как деление на 3, работа с таблицей цен.',
    russianGoal:
      'Прочитать слова: нужное, полезное, хочу, план, бюджет, осталось.',
    readingWritingGoal:
      'Прочитать 4 короткие фразы, написать 4 слова, составить план из 3 категорий с остатком.',
    practicalGoal:
      'Объяснить, почему план умнее, чем «купить всё сразу».',
    whatThisLessonTeaches:
      'Анита учится первой бюджетной логике: классифицировать покупки, считать сумму и остаток, сравнивать два плана и думать, что важнее.',
    keyMessage:
      'Умный покупатель не выбирает «всё сразу». Сначала нужное, потом полезное, потом «хочу» — и всегда есть остаток на цель.',
    howToExplain:
      'Возьмите 100 бумажных нимов и предметы из таблицы. Покажите, что разные планы могут стоить разное, даже если предметов столько же.',
    exampleDialogue:
      'Parent: Если ты купишь только нужное и полезное, сколько останется?\nChild: 120 − 28 − 36 = 56.\nParent: А если ещё «хочу» за 45?\nChild: 56 − 45 = 11.\nParent: Видишь, остаётся всего 11. Что лучше отложить?\nChild: «Хочу» можно купить позже.',
    readingSupport:
      'Длинные слова «полезное» и «бюджет» можно прочитать по слогам с родителем.',
    russianSpeechGoal:
      'Анита говорит по-русски: «План помогает, потому что сначала нужное, а «хочу» можно купить позже.»',
    mathAnswerKey:
      'Math 1: 28 + 36 + 45 = 109; 120 − 109 = 11 нимов.\nMath 2: A = 82; B = 90; B − A = 8 → A дешевле на 8.\nMath 3: 90 ÷ 3 = 30; 90 − 30 = 60.\nMath 4: 24 + 36 + 9 = 69; 80 − 69 = 11 → хватит, остаток 11.',
    spellingAnswerKey: 'нужное\nполезное\nплан\nбюджет',
    answerKey: [
      { taskId: 'reading-words', answer: 'Answer: попытка чтения 6 русских слов.\nSolution: Анита читает по очереди.\nParent note: Засчитывать честную попытку.' },
      { taskId: 'reading-sentences', answer: 'Answer: попытка чтения 4 фраз про план.\nSolution: Каждая фраза — отдельно.\nParent note: Английский только для смысла.' },
      { taskId: 'math-1-plan-120', answer: 'Answer: 11 нимов останется.\nSolution: 28 + 36 + 45 = 109; 120 − 109 = 11.\nParent note: Это сумма из трёх слагаемых + остаток.' },
      { taskId: 'math-2-compare-plans', answer: 'Answer: Вариант A дешевле на 8 нимов.\nSolution: A = 28 + 36 + 18 = 82; B = 28 + 45 + 17 = 90; 90 − 82 = 8.\nParent note: Подчеркните, что общая «куча» предметов не равна общей стоимости.' },
      { taskId: 'math-3-one-third-budget', answer: 'Answer: отложит 30, останется 60.\nSolution: 1/3 от 90 = 90 ÷ 3 = 30; 90 − 30 = 60.\nParent note: Связывайте 1/3 с делением «на 3 равные части».' },
      { taskId: 'math-4-price-table-budget', answer: 'Answer: стоимость 69 нимов, хватит, остаток 11.\nSolution: 24 + 36 + 9 = 69; 80 − 69 = 11.\nParent note: Ребёнок должен читать таблицу и складывать только нужные строки.' },
      { taskId: 'logic-plan-1-vs-plan-2', answer: 'Answer: умнее План 2 («нужное сейчас, хочу позже»).\nSolution: «Хочу» можно купить позже, а нужное — обязательно. Остаётся остаток на цель или на новые расходы.\nParent note: Принимается любое объяснение с этими идеями.' },
      { taskId: 'spelling-plan', answer: 'Answer: нужное, полезное, план, бюджет.\nSolution: Вставленные буквы: ж, л, а, ю.\nParent note: Мягкие ошибки допустимы.' },
      { taskId: 'creative-my-plan', answer: 'Answer: план на 100 нимов с тремя категориями и остатком.\nSolution: Любой набор, где нужное + полезное + хочу + остаток = 100.\nParent note: Главное — все три категории и правильная арифметика.' },
      { taskId: 'speech-explain-plan', answer: 'Answer: одна осмысленная русская фраза про план.\nSolution: «Сначала нужное, потом хочу. Остаётся на цель.»\nParent note: Главное — мысль, не идеальная грамматика.' }
    ],
    check: [
      'Анита прочитала русские слова и 4 фразы.',
      'Анита решила Math 1–4 с показом шагов.',
      'Анита выбрала умный план и объяснила почему.',
      'Анита написала 4 слова и составила свой план на 100 нимов.',
      'Анита сказала фразу про план по-русски.'
    ],
    spellingCheck:
      'Проверить буквы в словах нужное, полезное, план, бюджет.',
    readingCheck:
      'Засчитывать попытку чтения; помогать по слогам с «полезное» / «бюджет».',
    practicalCheck:
      'Анита делит покупки на «нужное / полезное / хочу» и понимает порядок.',
    drawingCriteria:
      'План содержит все три категории, числа складываются в 100, остаток показан.',
    commonMistakes: [
      '«Хочу = первое.» Правильно: сначала нужное.',
      '«Если хватает, надо брать всё.» Правильно: можно оставить остаток на цель.',
      'Складывать только два слагаемых из трёх. Правильно: сначала все три, потом остаток.'
    ],
    discussionPrompts: [
      'Что для тебя сегодня «нужное»?',
      'Что можно купить позже?',
      'Зачем оставлять остаток?'
    ],
    ifEasy:
      'Усложните: бюджет 150 нимов; нужное 42, полезное 38, хочу 55. Сколько останется? (Ответ: 150 − 135 = 15.) Можно ли отложить 1/3 бюджета и купить только нужное и полезное? (1/3 от 150 = 50; нужное+полезное = 80; 150 − 50 − 80 = 20.)',
    ifDifficult:
      'Оставьте только Math 1 и Math 3. Math 1 разберите вместе по шагам.',
    rewardGuidance:
      'Базовая награда — 3 нима. Бонус за самостоятельное сравнение двух планов или за объяснение выбора.',
    parentOnlyNotes:
      'Не превращайте занятие в обсуждение реальных семейных трат. Используйте только нимы и бумажные предметы.',
    tokenReward:
      'Нимы — это призовые токены курса, а не оплата.'
  },
  reward: {
    unit: 'nim',
    unitName: 'ним',
    base: 3,
    max: 5,
    label: 'Награда за миссию',
    display: '3 нима',
    bonuses: [
      { id: 'compare-plans', label: 'Бонус за сравнение двух планов', amount: 1 },
      { id: 'clear-plan-explanation', label: 'Бонус за хорошее объяснение плана', amount: 1 }
    ]
  },
  completionRequirements: {
    requiredTaskIds: [
      'reading-words',
      'reading-sentences',
      'math-1-plan-120',
      'math-2-compare-plans',
      'math-3-one-third-budget',
      'math-4-price-table-budget',
      'logic-plan-1-vs-plan-2',
      'spelling-plan',
      'creative-my-plan',
      'speech-explain-plan'
    ],
    allowNotebookCompletion: true
  },
  nextLessonId: 'anita-week-1-day-4'
};

export default day3;
